import { ref } from 'vue'
import type { Ref } from 'vue'
import type { Result } from '@/stores/type/result.type'
import type { ChartConfiguration } from '@/stores/type/chartConfiguration.type'
import { assembleChartData } from '@/views/calculations/utils/resultChartUtils'
import {
  CHART_EXPORT_FILE_TYPES,
  downloadChartReport,
  renderChartImageDataUrl,
  type ChartExportFileType
} from '@/views/calculations/utils/resultChartExportUtils'
import { useApi } from '@/api/useApi'
import API from '@/api/apiUrls'

export const useChartExportActions = (params: {
  resultItems: Ref<Result[]>
  calculationId: Ref<number>
  activeConfigId: Ref<number | null>
  activeFilters: Ref<string[]>
  activeGroupFilters: Ref<string[]>
}) => {
  const { resultItems, calculationId, activeConfigId, activeFilters, activeGroupFilters } = params
  const { api } = useApi()

  const exportDialog = ref(false)
  const exportDialogConfig = ref<ChartConfiguration | null>(null)

  const openChartExportDialog = (config: ChartConfiguration) => {
    exportDialogConfig.value = config
    exportDialog.value = true
  }

  const downloadResultsExcel = async () => {
    if (!calculationId.value) return
    const filename = `calculation-${calculationId.value}-results.xlsx`
    await api
      .service(API.CALCULATION.RESULTS.EXPORT_EXCEL(calculationId.value))
      .downloadBlob(null, filename)
  }

  const createExportReport = async (
    config: ChartConfiguration,
    fileType: ChartExportFileType,
    categoryFilters?: string[],
    groupFilters?: string[]
  ) => {
    const { labels, datasets, categories } = assembleChartData({
      results: resultItems.value,
      config,
      categoryFilters,
      groupFilters
    })
    const chartImageDataUrl = renderChartImageDataUrl({ config, labels, datasets })

    await downloadChartReport(
      {
        title: config.name || 'Chart',
        generatedAt: new Date(),
        xAxisGroup: config.x_axis_title || 'Location',
        yAxisGroup: config.y_axis_title || 'MKI [euro]',
        categories,
        chartImageDataUrl
      },
      fileType
    )
  }

  const downloadChartFromDialog = async (fileType: ChartExportFileType) => {
    if (!exportDialogConfig.value) return
    const isActiveConfig = exportDialogConfig.value.id === activeConfigId.value

    if (fileType === 'xlsx') {
      await downloadResultsExcel()
      exportDialog.value = false
      exportDialogConfig.value = null
      return
    }
    await createExportReport(
      exportDialogConfig.value,
      fileType,
      isActiveConfig ? activeFilters.value : undefined,
      isActiveConfig ? activeGroupFilters.value : undefined
    )
    exportDialog.value = false
    exportDialogConfig.value = null
  }

  return {
    exportDialog,
    exportDialogConfig,
    exportFileTypes: CHART_EXPORT_FILE_TYPES,
    openChartExportDialog,
    downloadChartFromDialog,
    downloadResultsExcel
  }
}
